// File: FixturePage.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import LeagueFixtures from "../components/LeagueFixtures";
import Poll from "../components/Poll";
import FormBreakdown from "../components/FormBreakdown";
import PredictionsSection from "../components/PredictionsSection";
import LineupsSection from "../components/LineupsSection";
import PlayersSection from "../components/PlayersSection";
import InjuriesSection from "../components/InjuriesSection";
import EventsSection from "../components/EventsSection";
import MatchStatsSection from "../components/MatchStatsSection";
import PlayerPropsSection from "../components/PlayerPropsSection";
import OddsTable from "../components/OddsTable";
import MatchPreview from "../components/MatchPreview";
import SportsNewsSection from "../components/SportsNewsSection";
import FixtureSocialPosts from "../components/FixtureSocialPosts";
import styles from "../styles/FixturePage.module.css";
import { slugifyTeamName } from "../utils/slugify";
import { api, fetchFixtureEdges, fetchLeagueTable } from "../api";
import { useAuth } from "../components/AuthGate";

// ⭐ preferences hook
import { usePreferences } from "../context/PreferencesContext";

// ⭐ pill component
import FixtureAccessPill from "../components/FixtureAccessPill";

// ⭐ team context panel
import TeamContextPanel from "../components/TeamContextPanel";

// ✅ NEW: match insights panel (you said you already made it)
import MatchInsights from "../components/MatchInsights";
import EdgeExplanationCard from "../components/EdgeExplanationCard";

// ✅ NEW: H2H section
import H2HSection from "../components/H2HSection";

import { placeAndTrackEdge } from "../utils/placeAndTrack";
import { getBookmakerUrl } from "../utils/bookmakers";

// ✅ odds display helper (decimal + fractional)
import { formatOddsBoth } from "../utils/oddsFormat";
import { hasCupContext } from "../utils/insightContext";
import { isAllowedPublicEdge } from "../utils/publicEdge";
import { formatMarketName } from "../utils/formatMarketName";
import { prettyCompetitionName } from "../utils/competitionNames";

const LIVE_REFRESH_DEBUG = process.env.NODE_ENV !== "production";
const FIXTURE_ENRICHMENT_WINDOW_DAYS = 7;

const isFixtureEnrichmentOpen = (fixture = {}) => {
  const raw = firstDefined(fixture.kickoff_utc, fixture.date, fixture.fixture_date);
  if (!raw) return false;
  const kickoff = new Date(raw);
  if (Number.isNaN(kickoff.getTime())) return false;
  return kickoff.getTime() <= Date.now() + FIXTURE_ENRICHMENT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
};

const buildOddsFingerprint = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) =>
      [
        String(row?.market ?? ""),
        String(row?.bookmaker ?? ""),
        String(row?.selection ?? ""),
        String(row?.price ?? ""),
        String(row?.last_seen ?? ""),
      ].join("|")
    )
    .sort()
    .join("||");

const getLatestOddsLastSeen = (rows = []) => {
  let latest = null;
  for (const row of Array.isArray(rows) ? rows : []) {
    const raw = row?.last_seen;
    if (!raw) continue;
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) continue;
    if (!latest || dt.getTime() > latest.getTime()) latest = dt;
  }
  return latest;
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const isWorldCupCompetition = (competition, providerLeagueId) => {
  const normalized = String(competition || "")
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, "");
  return Number(providerLeagueId) === 1 || normalized === "WC" || normalized === "WORLDCUP" || normalized === "FIFAWORLDCUP";
};

const normalizeStandingRow = (row = {}, preserveNulls = false) => {
  const emptyStat = preserveNulls ? null : 0;
  const all = firstDefined(row.all, row.overall, row.stats?.all, {});
  return {
    position: firstDefined(row.position, row.rank, row.pos, "-"),
    team: firstDefined(row.team?.name, row.team_name, row.name, typeof row.team === "string" ? row.team : null, "Unknown"),
    team_id: firstDefined(row.team_id, row.provider_team_id, row.team?.id, row.id, null),
    team_logo: firstDefined(row.team_logo, row.logo, row.team?.logo, row.team_badge, null),
    played: firstDefined(row.played, row.p, row.mp, all.played, emptyStat),
    win: firstDefined(row.win, row.w, all.win, emptyStat),
    draw: firstDefined(row.draw, row.d, all.draw, emptyStat),
    lose: firstDefined(row.lose, row.loss, row.l, all.lose, emptyStat),
    gf: firstDefined(row.gf, row.goals_for, row.goals?.for, all.goals?.for, emptyStat),
    ga: firstDefined(row.ga, row.goals_against, row.goals?.against, all.goals?.against, emptyStat),
    points: firstDefined(row.points, row.pts, emptyStat),
    form: firstDefined(row.form, row.recent_form, ""),
    group: firstDefined(row.group, row.stage_group, ""),
  };
};

const normalizeWorldCupTableGroups = (payload = {}) => {
  const groups = Array.isArray(payload.table_groups) ? payload.table_groups : [];
  const normalized = groups
    .map((group, index) => ({
      name: firstDefined(group.group, group.name, group.label, `Group ${index + 1}`),
      rows: (group.table || group.rows || []).map((row) => normalizeStandingRow(row, true)),
    }))
    .filter((group) => group.rows.length && /^group\s+[a-l]$/i.test(group.name));

  return normalized.sort((a, b) => a.name.localeCompare(b.name));
};

const normalizeTeamName = (name) =>
  String(name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "")
    .trim();

const statDisplay = (value) =>
  value === undefined || value === null || value === "" ? "-" : value;

const goalDifference = (row = {}) => {
  const gf = Number(row.gf);
  const ga = Number(row.ga);
  return Number.isFinite(gf) && Number.isFinite(ga) ? gf - ga : "-";
};

const FINISHED_STATUSES = new Set(["FT", "AET", "PEN"]);
const LIVE_STATUSES = new Set(["1H", "HT", "2H", "ET", "BT", "P", "LIVE"]);

const fixtureStatusCode = (fixture = {}) =>
  String(firstDefined(fixture.status_short, fixture.status, fixture.fixture_status, "NS")).toUpperCase();

const fixtureScore = (fixture = {}, side) =>
  firstDefined(
    fixture[`${side}_score`],
    fixture.goals?.[side],
    fixture.score?.fulltime?.[side],
    fixture.score?.halftime?.[side],
    null
  );

const fixtureLogo = (fixture = {}, side) =>
  firstDefined(
    fixture[`${side}_logo`],
    fixture[`${side}_team_logo`],
    fixture.teams?.[side]?.logo,
    null
  );

const eventMinute = (event = {}) => {
  const elapsed = Number(firstDefined(event.elapsed, event.time?.elapsed, null));
  const extra = Number(firstDefined(event.extra, event.time?.extra, null));
  if (!Number.isFinite(elapsed)) return "";
  return `${elapsed}${Number.isFinite(extra) && extra > 0 ? `+${extra}` : ""}'`;
};

const StandingsTable = ({ rows, homeTeam, awayTeam, compact = false }) => (
  <div className="scrollX">
    <table className={`${styles.oddsTable} ${compact ? styles.groupStandingsTable : ""}`}>
      <thead>
        <tr>
          <th>#</th>
          <th>Team</th>
          <th>P</th>
          <th>W</th>
          <th>D</th>
          <th>L</th>
          <th>GF</th>
          <th>GA</th>
          <th>GD</th>
          <th>Pts</th>
          {!compact && <th>Form</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const isHome = normalizeTeamName(row.team) === normalizeTeamName(homeTeam);
          const isAway = normalizeTeamName(row.team) === normalizeTeamName(awayTeam);
          const rowClass = isHome || isAway ? styles.highlightRow : "";
          const fallbackLogo = `/logos/${slugifyTeamName(row.team)}.png`;

          return (
            <tr key={`${row.team_id || row.team}-${index}`} className={rowClass}>
              <td>{statDisplay(row.position) === "-" ? index + 1 : row.position}</td>
              <td className={styles.teamName}>
                <span className={styles.standingsTeam}>
                  <img
                    src={row.team_logo || fallbackLogo}
                    alt=""
                    className={styles.tableLogo}
                    onError={(event) => {
                      if (event.currentTarget.dataset.usedFallback === "true") {
                        event.currentTarget.style.display = "none";
                        return;
                      }
                      event.currentTarget.dataset.usedFallback = "true";
                      event.currentTarget.src = fallbackLogo;
                    }}
                  />
                  {row.team_id ? (
                    <Link
                      to={`/club/${row.team_id}/${slugifyTeamName(row.team)}`}
                      className={styles.teamLink}
                      title={`Open ${row.team}`}
                    >
                      {row.team}
                    </Link>
                  ) : (
                    <span className={styles.teamLink}>{row.team}</span>
                  )}
                </span>
              </td>
              <td>{statDisplay(row.played)}</td>
              <td>{statDisplay(row.win)}</td>
              <td>{statDisplay(row.draw)}</td>
              <td>{statDisplay(row.lose)}</td>
              <td>{statDisplay(row.gf)}</td>
              <td>{statDisplay(row.ga)}</td>
              <td>{goalDifference(row)}</td>
              <td className={styles.standingsPoints}>{statDisplay(row.points)}</td>
              {!compact && <td>{row.form || "-"}</td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const FixturePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ⭐ favourites from context
  const { favoriteSports, favoriteTeams, favoriteLeagues, updatePreferences } =
    usePreferences();

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/fixtures");
  };

  const fixtureIdNum = Number(id);
  const isAdminView =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/admin");

  const [data, setData] = useState(null);
  const [leagueFixtures, setLeagueFixtures] = useState([]);
  const [formData, setFormData] = useState(null);
  const [formN, setFormN] = useState(5);
  const [formScopeAll, setFormScopeAll] = useState(0);
  const [leagueTable, setLeagueTable] = useState([]);
  const [leagueTableGroups, setLeagueTableGroups] = useState([]);
  const [leagueTableLoading, setLeagueTableLoading] = useState(false);
  const [leagueTableError, setLeagueTableError] = useState("");
  const [activeTab, setActiveTab] = useState("preview");
  const [lineupTab, setLineupTab] = useState("lineup");
  const [selectedBookmaker, setSelectedBookmaker] = useState("All");

  const [edges, setEdges] = useState([]);
  const [edgesMeta, setEdgesMeta] = useState(null); // freemium meta
  const [explanations, setExplanations] = useState({});
  const [expandedWhy, setExpandedWhy] = useState(null);
  const [expandedWhyMarket, setExpandedWhyMarket] = useState(null);
  const [explainFormWindow, setExplainFormWindow] = useState(5);
  const [explanationLoadingKey, setExplanationLoadingKey] = useState("");
  const [explanationErrors, setExplanationErrors] = useState({});

  // “Adding…” loading state on individual edge
  const [placingKey, setPlacingKey] = useState(null);

  // ✅ NEW: Match Insights state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // ✅ NEW: H2H state
  const [h2hData, setH2hData] = useState(null);
  const [h2hLoading, setH2hLoading] = useState(false);

  // Live in-play model state (fetched lazily when tab is opened)
  const [liveData, setLiveData] = useState(null);   // full /inplay response or null
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState("");
  const [liveRefreshNotice, setLiveRefreshNotice] = useState("");
  const [lastLiveRefreshAt, setLastLiveRefreshAt] = useState(null);
  const dataEnrichmentOpen = isFixtureEnrichmentOpen(data?.fixture);

  const formatKickoff = (utcString) => {
    if (!utcString) return "";
    const s = utcString.endsWith("Z") ? utcString : utcString + "Z";
    return new Date(s).toLocaleString(navigator.language, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  const normalizeExplainMarket = (raw) => {
    if (!raw) return "";
    let x = String(raw).toUpperCase().replace(/\s+/g, "");
    const syn = {
      // BTTS
      BTTSYES: "BTTS_Y",
      BTTSY: "BTTS_Y",
      BTTSNO: "BTTS_N",
      BTTSN: "BTTS_N",

      // 1X2
      HOMEWIN: "HOME_WIN",
      AWAYWIN: "AWAY_WIN",

      // DNB (Draw No Bet)
      HOMEDNB: "HOME_DNB",
      HOME_DNB: "HOME_DNB",
      DRAWNOBETHOME: "HOME_DNB",
      HOMEDRAWNOBET: "HOME_DNB",

      AWAYDNB: "AWAY_DNB",
      AWAY_DNB: "AWAY_DNB",
      DRAWNOBETAWAY: "AWAY_DNB",
      AWAYDRAWNOBET: "AWAY_DNB",
    };

    if (syn[x]) return syn[x];

    const ou =
      x.match(/^O?VER?(\d+(\.\d+)?)$/) ||
      x.match(/^U?N?DER?(\d+(\.\d+)?)$/);
    if (ou) {
      if (/^O(VER)?\d/.test(x)) return `O${x.replace(/^O(VER)?/, "")}`;
      if (/^U(NDER)?\d/.test(x)) return `U${x.replace(/^U(NDER)?/, "")}`;
    }

    if (/^O\d+(\.\d+)?$/.test(x) || /^U\d+(\.\d+)?$/.test(x)) return x;

    return x;
  };

  const getExplainNotes = (explanation, edge) => {
    const rowWhy = getRowWhy(edge);
    if (rowWhy) return [cleanMarketKeysInText(rowWhy)];
    if (isStatPropMarket(edge?.market)) return [buildStatPropFallbackWhy(edge)];
    if (Array.isArray(explanation?.notes) && explanation.notes.length) {
      return explanation.notes.map((line) => cleanMarketKeysInText(line));
    }
    return [];
  };

  const isStatPropMarket = (market) =>
    /^(?:(HOME|AWAY)_)?(CORNERS|CARDS|SOG)_[OU]\d+(\.\d+)?$/i.test(String(market || "").trim());

  const getRowWhy = (edge) =>
    edge?.why || edge?.explanation || edge?.reason || edge?.why_text || "";

  const cleanMarketKeysInText = (input) =>
    String(input ?? "").replace(
      /\b(?:(?:HOME|AWAY)_)?(?:CORNERS|CARDS|SOG)_[OU]\d+(?:\.\d+)?\b/g,
      (match) => formatMarketName(match)
    );

  const buildStatPropFallbackWhy = (edge) => {
    const marketLabel = formatMarketName(edge?.market);
    const modelProb = Number(edge?.prob);
    const price = Number(edge?.price);
    const edgeValue = Number(edge?.edge);
    const bookmaker = edge?.bookmaker || "the market";
    const modelText = Number.isFinite(modelProb) ? `${(modelProb * 100).toFixed(1)}%` : null;
    const impliedText = Number.isFinite(price) && price > 0 ? `${((1 / price) * 100).toFixed(1)}%` : null;
    const edgeText = Number.isFinite(edgeValue) ? `${(edgeValue * 100).toFixed(1)}%` : null;

    if (modelText && impliedText && edgeText) {
      return `Model predicts a ${modelText} chance for ${marketLabel}, but ${bookmaker} odds imply ${impliedText}, creating a ${edgeText} edge.`;
    }
    if (modelText && edgeText) {
      return `Model predicts a ${modelText} chance for ${marketLabel}, creating a ${edgeText} edge.`;
    }
    if (modelText) {
      return `Model predicts a ${modelText} chance for ${marketLabel}.`;
    }
    return `${marketLabel} is priced with a model edge on this row.`;
  };

  const getExplanationCacheKey = (rawMarket, formWindow = explainFormWindow) =>
    `${String(rawMarket || "").toUpperCase()}__${formWindow}`;

  // --- LOAD FIXTURE DATA ----------------------------------------------------
  useEffect(() => {
    const fetchFixture = async () => {
      try {
        const { data: fx } = await api.get(`/api/fixtures/id/${id}/json`);
        setData(fx);

        if (!isFixtureEnrichmentOpen(fx?.fixture)) {
          setLeagueFixtures([]);
          setFormData(null);
          return;
        }

        const { data: roundData } = await api.get(`/api/fixtures/round`, {
          params: { fixture_id: id },
        });
        setLeagueFixtures(roundData);

        const { data: fj } = await api.get(`/form/form/fixture`, {
          params: { fixture_id: id, n: formN, all_comps: formScopeAll },
        });

        setFormData({
          home:
            fj.home ??
            { summary: fj.home_form ?? {}, recent: fj.home_recent ?? [] },
          away:
            fj.away ??
            { summary: fj.away_form ?? {}, recent: fj.away_recent ?? [] },
          n: fj.n ?? formN,
        });
      } catch (err) {
        console.error("Error fetching fixture:", err);
      }
    };
    fetchFixture();
  }, [id, formN, formScopeAll]);

  // --- LOAD LEAGUE TABLE ----------------------------------------------------
  useEffect(() => {
    if (!data?.fixture?.comp || !dataEnrichmentOpen) {
      setLeagueTable([]);
      setLeagueTableGroups([]);
      return;
    }
    let alive = true;
    const fetchTable = async () => {
      setLeagueTableLoading(true);
      setLeagueTableError("");
      try {
        const worldCup = isWorldCupCompetition(
          data.fixture.comp,
          data.fixture.provider_league_id
        );
        if (worldCup) {
          const tableJson = await fetchLeagueTable("WC", 2026);
          if (!alive) return;
          const groups = normalizeWorldCupTableGroups(tableJson);
          setLeagueTableGroups(groups);
          setLeagueTable(groups.flatMap((group) => group.rows));
          return;
        }

        const { data: tableJson } = await api.get(`/api/fixtures/league/table`, {
          params: { league: data.fixture.comp },
        });
        if (!alive) return;
        const rows = Array.isArray(tableJson) ? tableJson : tableJson.table || [];
        setLeagueTable(rows.map((row) => normalizeStandingRow(row)));
        setLeagueTableGroups([]);
      } catch (err) {
        console.error("Error loading league table:", err);
        if (!alive) return;
        setLeagueTableError("Could not load the standings right now.");
        setLeagueTable([]);
        setLeagueTableGroups([]);
      } finally {
        if (alive) setLeagueTableLoading(false);
      }
    };
    fetchTable();
    return () => {
      alive = false;
    };
  }, [data?.fixture?.comp, data?.fixture?.provider_league_id, dataEnrichmentOpen]);

  // --- LOAD EDGES (freemium endpoint) --------------------------------------
  useEffect(() => {
    const loadEdges = async () => {
      try {
        const json = await fetchFixtureEdges(fixtureIdNum);
        const fullEdges = json.edges || json.edges_teaser || [];

        setEdges(
          Array.isArray(fullEdges)
            ? fullEdges.filter((edge) => isAllowedPublicEdge(edge?.edge))
            : []
        );
        setEdgesMeta({
          isPremium: !!json.is_premium,
          hasAccess: !!json.has_access,
          usedToday: json.used_today ?? 0,
          limit: json.limit ?? 0,
          hasFullAccess: !!(json.is_premium || json.has_access),
          isTeaser: !json.is_premium && !json.has_access,
          authMismatch: !!json?._auth?.authMismatch,
          signedIn: !!json?._auth?.signedIn,
          meIsPremium: !!json?._auth?.meIsPremium,
          retriedWithFreshToken: !!json?._auth?.retriedWithFreshToken,
        });
      } catch (err) {
        console.error("Error fetching edges:", err);
        setEdges([]);
        setEdgesMeta(null);
      }
    };
    if (fixtureIdNum && dataEnrichmentOpen) loadEdges();
    else {
      setEdges([]);
      setEdgesMeta(null);
    }
  }, [fixtureIdNum, dataEnrichmentOpen]);

  // ✅ LOAD MATCH INSIGHTS (fair prices) -------------------------------------
  useEffect(() => {
  const loadInsights = async () => {
    if (!fixtureIdNum || !dataEnrichmentOpen) {
      setInsights(null);
      return;
    }
    setInsightsLoading(true);
    try {
      const { data: json } = await api.get(
        `/api/fixtures/${fixtureIdNum}/insights`,
        { params: { model: "team_form" } } // or whatever model you want
      );
      setInsights(json || null);
    } catch (err) {
      console.error("Error fetching match insights:", err);
      setInsights(null);
    } finally {
      setInsightsLoading(false);
    }
  };

  loadInsights();
}, [fixtureIdNum, dataEnrichmentOpen]);

  // ✅ LOAD H2H DATA ----------------------------------------------------------
  useEffect(() => {
    const loadH2H = async () => {
      if (!fixtureIdNum || !dataEnrichmentOpen) {
        setH2hData(null);
        return;
      }
      setH2hLoading(true);
      try {
        const { data: json } = await api.get(`/football/h2h/${fixtureIdNum}`);
        setH2hData(json || null);
      } catch (err) {
        console.error("Error fetching H2H data:", err);
        setH2hData(null);
      } finally {
        setH2hLoading(false);
      }
    };

    loadH2H();
  }, [fixtureIdNum, dataEnrichmentOpen]);

  // ✅ LOAD LIVE IN-PLAY MODEL (lazy — only when tab is active) ---------------
  useEffect(() => {
    if (activeTab !== "live" || !fixtureIdNum) return;
    let cancelled = false;
    const load = async () => {
      setLiveLoading(true);
      setLiveError("");
      try {
        const { data: json } = await api.get(`/api/fixtures/id/${fixtureIdNum}/inplay`);
        if (!cancelled) setLiveData(json || null);
      } catch (err) {
        if (!cancelled) {
          console.error("Live fetch failed:", err);
          setLiveError("Could not fetch live data.");
          setLiveData(null);
        }
      } finally {
        if (!cancelled) setLiveLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeTab, fixtureIdNum]);

  const refreshLiveOdds = async (source = "manual") => {
    if (!fixtureIdNum) return;
    const ts = Date.now();
    const previousFingerprint = buildOddsFingerprint(data?.odds || []);

    if (LIVE_REFRESH_DEBUG) {
      console.log("[LiveRefresh] click", {
        source,
        routeFixtureId: fixtureIdNum,
        pageFixtureId: data?.fixture?.id,
      });
    }

    setLiveLoading(true);
    setLiveError("");
    setLiveRefreshNotice("");

    try {
      if (LIVE_REFRESH_DEBUG) {
        console.log("[LiveRefresh] request:start", {
          fixtureId: fixtureIdNum,
          inplayUrl: `/api/fixtures/id/${fixtureIdNum}/inplay`,
          oddsUrl: `/api/fixtures/id/${fixtureIdNum}/json`,
          ts,
        });
      }

      const [inplayRes, fixtureRes] = await Promise.all([
        api.get(`/api/fixtures/id/${fixtureIdNum}/inplay`, { params: { _ts: ts } }),
        api.get(`/api/fixtures/id/${fixtureIdNum}/json`, { params: { _ts: ts } }),
      ]);

      const freshPayload = fixtureRes?.data || {};
      const freshOdds = Array.isArray(freshPayload.odds) ? freshPayload.odds : [];
      const freshFingerprint = buildOddsFingerprint(freshOdds);

      setLiveData(inplayRes?.data || null);
      setData(freshPayload);
      setLastLiveRefreshAt(new Date());
      setLiveRefreshNotice(
        freshFingerprint === previousFingerprint
          ? "No price change detected."
          : "Updated just now."
      );

      if (LIVE_REFRESH_DEBUG) {
        console.log("[LiveRefresh] response:received", {
          fixtureId: fixtureIdNum,
          oddsCount: freshOdds.length,
          changed: freshFingerprint !== previousFingerprint,
          latestLastSeen: getLatestOddsLastSeen(freshOdds)?.toISOString() || null,
        });
      }
    } catch (err) {
      console.error("Live refresh failed:", err);
      setLiveError(
        err?.response?.data?.detail || err?.message || "Could not refresh live data."
      );
      setLiveRefreshNotice("");
    } finally {
      setLiveLoading(false);
    }
  };

  // --- WHY EXPLANATION ------------------------------------------------------
  const fetchExplanation = async (rawMarket, index, options = {}) => {
    const force = Boolean(options.force);
    const edge = options.edge || {};
    const cacheKey = getExplanationCacheKey(rawMarket);
    if (!force && (getRowWhy(edge) || isStatPropMarket(rawMarket))) {
      const isSameOpen = expandedWhy === index && expandedWhyMarket === rawMarket;
      setExpandedWhy(isSameOpen ? null : index);
      setExpandedWhyMarket(isSameOpen ? null : rawMarket);
      setExplanationErrors((prev) => ({ ...prev, [cacheKey]: "" }));
      return;
    }
    if (!force && explanations[cacheKey]) {
      const isSameOpen = expandedWhy === index && expandedWhyMarket === rawMarket;
      setExpandedWhy(isSameOpen ? null : index);
      setExpandedWhyMarket(isSameOpen ? null : rawMarket);
      return;
    }
    const market = normalizeExplainMarket(rawMarket);
    try {
      setExplanationLoadingKey(cacheKey);
      setExplanationErrors((prev) => ({ ...prev, [cacheKey]: "" }));
      const { data: json } = await api.get(`/explain/probability`, {
        params: { fixture_id: id, market, form_n: explainFormWindow },
      });
      setExplanations((prev) => ({ ...prev, [cacheKey]: json }));
      setExpandedWhy(index);
      setExpandedWhyMarket(rawMarket);
    } catch (err) {
      console.error("Error loading explanation:", err);
      setExplanationErrors((prev) => ({
        ...prev,
        [cacheKey]: err?.response?.data?.detail || err?.message || "Could not load explanation.",
      }));
      setExpandedWhy(index);
      setExpandedWhyMarket(rawMarket);
    } finally {
      setExplanationLoadingKey((prev) => (prev === cacheKey ? "" : prev));
    }
  };

  useEffect(() => {
    if (expandedWhy === null || !expandedWhyMarket) return;
    if (isStatPropMarket(expandedWhyMarket)) return;
    fetchExplanation(expandedWhyMarket, expandedWhy, { force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explainFormWindow]);

  // --- PLACE BET BUTTON -----------------------------------------------------
  const handlePlaceEdge = async (edge, key) => {
    if (!user) {
      navigate("/login");
      return;
    }

    const bmUrl = getBookmakerUrl(edge.bookmaker);
    const openUrl = bmUrl
      ? bmUrl
      : "https://google.com/search?q=" + encodeURIComponent(edge.bookmaker);

    window.open(openUrl, "_blank", "noopener");

    try {
      setPlacingKey(key);
      await placeAndTrackEdge({ ...edge, fixture_id: fixtureIdNum }, { stake: 1 });
      navigate("/bets");
    } catch (err) {
      console.error("Failed to place bet:", err);
      alert("Could not place this bet. Try again.");
    } finally {
      setPlacingKey(null);
    }
  };

  // --- FAVOURITES TOGGLES ---------------------------------------------------
  const favTeams = favoriteTeams || [];
  const favLeagues = favoriteLeagues || [];
  const favSports = favoriteSports || [];

  const toggleFavouriteTeam = async (teamName) => {
    if (!user) {
      navigate("/login");
      return;
    }
    const exists = favTeams.includes(teamName);
    const nextTeams = exists
      ? favTeams.filter((t) => t !== teamName)
      : [...favTeams, teamName];

    try {
      await updatePreferences({
        favoriteSports: favSports,
        favoriteTeams: nextTeams,
        favoriteLeagues: favLeagues,
      });
    } catch (err) {
      console.error("Failed to update favourite teams from fixture", err);
    }
  };

  const toggleFavouriteLeague = async (leagueCode) => {
    if (!user) {
      navigate("/login");
      return;
    }
    const exists = favLeagues.includes(leagueCode);
    const nextLeagues = exists
      ? favLeagues.filter((l) => l !== leagueCode)
      : [...favLeagues, leagueCode];

    try {
      await updatePreferences({
        favoriteSports: favSports,
        favoriteTeams: favTeams,
        favoriteLeagues: nextLeagues,
      });
    } catch (err) {
      console.error("Failed to update favourite leagues from fixture", err);
    }
  };

  if (!data) return <p className={styles.loading}>Loading…</p>;
  if (!data.fixture) return <p className={styles.error}>Fixture not found.</p>;

  const fixture = data.fixture;
  const odds = data.odds ?? [];
  const latestOddsSeen = getLatestOddsLastSeen(odds);

  const homeTeamId =
    fixture.provider_home_team_id ??
    fixture.home_team_id ??
    fixture.home_id ??
    fixture.homeTeamId;

  const awayTeamId =
    fixture.provider_away_team_id ??
    fixture.away_team_id ??
    fixture.away_id ??
    fixture.awayTeamId;

  const season = fixture.season ?? fixture.league_season ?? fixture.year ?? 2025;

  const leagueId =
    fixture.league_id ?? fixture.provider_league_id ?? fixture.api_league_id ?? null;

  const isHomeFav = favTeams.includes(fixture.home_team);
  const isAwayFav = favTeams.includes(fixture.away_team);
  const isLeagueFav = favLeagues.includes(fixture.comp);

  const grouped = Array.isArray(odds)
    ? odds.reduce((acc, o) => {
        (acc[o.market] ||= []).push(o);
        return acc;
      }, {})
    : {};

  const uniqueBookmakers = [...new Set(edges.map((e) => e.bookmaker))];
  const filteredEdges =
    selectedBookmaker === "All"
      ? edges
      : edges.filter((e) => e.bookmaker === selectedBookmaker);

  const clubHref = (teamId, teamName) => {
    if (!teamId) return null;
    const slug = slugifyTeamName(teamName);
    const params = new URLSearchParams();
    if (season) params.set("season", String(season));
    if (leagueId) params.set("league", String(leagueId));
    const qs = params.toString();
    return `/club/${teamId}/${slug}${qs ? `?${qs}` : ""}`;
  };

  const statusCode = fixtureStatusCode(fixture);
  const isFinished = FINISHED_STATUSES.has(statusCode);
  const isLive = LIVE_STATUSES.has(statusCode);
  const homeScore = fixtureScore(fixture, "home");
  const awayScore = fixtureScore(fixture, "away");
  const hasScore = homeScore !== null && awayScore !== null;
  const competitionName = prettyCompetitionName(fixture.comp, "Football");
  const isFootballFixture =
    !fixture.sport || ["football", "soccer"].includes(String(fixture.sport).toLowerCase());
  const rawEvents = Array.isArray(fixture.events)
    ? fixture.events
    : Array.isArray(data.events)
      ? data.events
      : [];
  const goalEvents = rawEvents
    .filter((event) => String(event?.type || "").toLowerCase() === "goal")
    .slice(0, 5);

  const shareFixture = async () => {
    const shareData = {
      title: `${fixture.home_team} vs ${fixture.away_team}`,
      text: `${fixture.home_team} vs ${fixture.away_team} on CSB`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch (err) {
      if (err?.name !== "AbortError") console.error("Unable to share fixture:", err);
    }
  };

  const kickoffDate = fixture.kickoff_utc ? new Date(fixture.kickoff_utc) : null;
  const enrichmentOpen = isFixtureEnrichmentOpen(fixture);
  const daysUntilKickoff = kickoffDate && !Number.isNaN(kickoffDate.getTime())
    ? Math.max(0, Math.ceil((kickoffDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;
  const analyticalTabs = new Set(["live", "h2h", "table", "predictions", "lineups", "stats", "players", "events"]);
  const heroScore = hasScore
    ? `${homeScore} - ${awayScore}`
    : kickoffDate && !Number.isNaN(kickoffDate.getTime())
      ? kickoffDate.toLocaleTimeString(navigator.language, {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "vs";

  return (
    <div className={`${styles.page} scrollX`}>
      <header className={`${styles.matchHero} ${isFootballFixture ? styles.footballHero : ""}`}>
        <div className={styles.heroPitchLines} aria-hidden="true" />
        <div className={styles.heroActionRow}>
          <button type="button" className={styles.heroIconButton} onClick={goBack} title="Back to fixtures">
            ←
          </button>
          <div className={styles.heroCompetition}>
            <span>{competitionName}</span>
            <strong>{fixture.round || fixture.fixture_round || formatKickoff(fixture.kickoff_utc)}</strong>
          </div>
          <div className={styles.heroActions}>
            <button
              type="button"
              className={`${styles.heroIconButton} ${isLeagueFav ? styles.heroIconActive : ""}`}
              onClick={() => toggleFavouriteLeague(fixture.comp)}
              title={isLeagueFav ? "League saved" : "Save league"}
            >
              {isLeagueFav ? "★" : "☆"}
            </button>
            <button type="button" className={styles.heroIconButton} onClick={shareFixture} title="Share match">
              ↗
            </button>
          </div>
        </div>

        <div className={styles.heroScoreboard}>
          {[
            {
              side: "home",
              name: fixture.home_team,
              id: homeTeamId,
              logo: fixtureLogo(fixture, "home"),
              saved: isHomeFav,
            },
            {
              side: "away",
              name: fixture.away_team,
              id: awayTeamId,
              logo: fixtureLogo(fixture, "away"),
              saved: isAwayFav,
            },
          ].map((team, index) => (
            <React.Fragment key={team.side}>
              {index === 1 && (
                <div className={styles.heroScore}>
                  <strong>{heroScore}</strong>
                  <span className={isLive ? styles.liveStatus : ""}>
                    {isFinished ? "FT" : isLive ? `${fixture.elapsed || statusCode}'` : formatKickoff(fixture.kickoff_utc)}
                  </span>
                </div>
              )}
              <div className={styles.heroTeam}>
                {clubHref(team.id, team.name) ? (
                  <Link to={clubHref(team.id, team.name)} className={styles.heroTeamLink}>
                    <div className={styles.heroLogoShell}>
                      <img
                        src={team.logo || `/logos/${slugifyTeamName(team.name)}.png`}
                        alt=""
                        className={styles.heroTeamLogo}
                        onError={(event) => {
                          const fallback = `/logos/${slugifyTeamName(team.name)}.png`;
                          if (event.currentTarget.dataset.fallback === "true") {
                            event.currentTarget.style.display = "none";
                          } else {
                            event.currentTarget.dataset.fallback = "true";
                            event.currentTarget.src = fallback;
                          }
                        }}
                      />
                    </div>
                    <strong>{team.name}</strong>
                  </Link>
                ) : (
                  <div className={styles.heroTeamLink}>
                    <div className={styles.heroLogoShell}>
                      <img
                        src={team.logo || `/logos/${slugifyTeamName(team.name)}.png`}
                        alt=""
                        className={styles.heroTeamLogo}
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                    <strong>{team.name}</strong>
                  </div>
                )}
                <button
                  type="button"
                  className={`${styles.teamSaveButton} ${team.saved ? styles.teamSaveActive : ""}`}
                  onClick={() => toggleFavouriteTeam(team.name)}
                >
                  {team.saved ? "★ Following" : "☆ Follow"}
                </button>
              </div>
            </React.Fragment>
          ))}
        </div>

        {!!goalEvents.length && (
          <div className={styles.heroScorers}>
            {goalEvents.map((event, index) => (
              <span key={event.id || `${event.player_name || event.player?.name}-${index}`}>
                {event.player_name || event.player?.name || "Goal"} {eventMinute(event)}
                {String(event.detail || "").toLowerCase().includes("penalty") ? " (Pen)" : ""}
              </span>
            ))}
          </div>
        )}

        {isFootballFixture && enrichmentOpen && (
          <div className={styles.quickMatchTabs}>
            {[
              ["lineups", "Lineups"],
              ["stats", "Stats"],
              ["events", "Timeline"],
            ].map(([tab, label]) => (
              <button
                type="button"
                key={tab}
                className={activeTab === tab ? styles.quickMatchTabActive : ""}
                onClick={() => setActiveTab(tab)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </header>

      <nav className={styles.tabs} aria-label="Match sections">
        {[
          ["preview", "Preview"],
          ["live", "Live Odds"],
          ["h2h", "H2H"],
          ["table", "Table"],
          ["predictions", "Predictions"],
          ["lineups", "Lineups"],
          ["stats", "Stats"],
          ["players", "Players"],
          ["events", "Timeline"],
        ].filter(([tab]) => enrichmentOpen || !analyticalTabs.has(tab)).map(([tab, label]) => (
          <button
            type="button"
            key={tab}
            className={activeTab === tab ? styles.activeTab : ""}
            onClick={() => setActiveTab(tab)}
          >
            {label}
          </button>
        ))}
      </nav>

      <div className={styles.body}>
        <div className={styles.main}>
          {activeTab === "preview" && (
            <>
              {!enrichmentOpen ? (
                <div className={styles.tabContent} style={{ padding: 24 }}>
                  <div style={{ maxWidth: 720 }}>
                    <span style={{ color: "#34d399", fontWeight: 900, letterSpacing: ".12em", textTransform: "uppercase", fontSize: 12 }}>Matchday shell is open</span>
                    <h2 style={{ margin: "8px 0", color: "#f8fafc" }}>The conversation starts before the data does.</h2>
                    <p style={{ color: "#a7b7ae", lineHeight: 1.6, margin: 0 }}>
                      Follow the fixture, vote and join the fan conversation now. Form, model edges, lineups, injuries, stats and predictions unlock automatically seven days before kickoff{daysUntilKickoff !== null ? ` (${daysUntilKickoff} days away)` : ""}.
                    </p>
                  </div>
                </div>
              ) : (
                <div className={styles.tabContent}>
                  <MatchPreview fixtureId={fixtureIdNum} isAdmin={isAdminView} />
                </div>
              )}

              {/* ✅ NEW: Match Insights block */}
              {enrichmentOpen && <div style={{ margin: "12px 0 16px" }}>
                <MatchInsights
                  fixtureId={fixtureIdNum}
                  fixture={fixture}
                  insights={insights}
                  loading={insightsLoading}
                />
              </div>}

              <div
                style={{
                  margin: "12px 0 16px",
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background:
                    "linear-gradient(135deg, rgba(20,83,45,0.8), rgba(15,23,42,0.9))",
                  color: "#e5f4eb",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontSize: 13, flex: 1, minWidth: 200 }}>
                  <strong>Player stats & props</strong>
                  <br />
                  See shots, cards and season breakdowns for every player in this match.
                </div>
                <button
                  onClick={() => setActiveTab("players")}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: "1px solid rgba(34,197,94,0.9)",
                    background: "rgba(22,163,74,0.9)",
                    color: "#fff",
                    fontSize: 13,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Open player stats →
                </button>
              </div>

              <div className={styles.bestEdges}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <h3>Best Edges</h3>
                  {edgesMeta && (
                    <FixtureAccessPill
                      meta={edgesMeta}
                      fixtureId={fixtureIdNum}
                      variant="default"
                    />
                  )}
                </div>

                {edgesMeta && (
                  <p className={styles.edgesMeta}>
                    {edgesMeta.hasFullAccess ? (
                      edgesMeta.isPremium ? (
                        <>
                          You’re on <b>CSB Premium</b> – full model edges unlocked for this
                          fixture.
                        </>
                      ) : (
                        <>
                          This fixture is unlocked. You’ve used <b>{edgesMeta.usedToday}</b> /{" "}
                          <b>{edgesMeta.limit}</b> free fixture unlocks today.
                        </>
                      )
                    ) : (
                      edgesMeta.authMismatch ? (
                        <>
                          You’re signed in and your Premium status was detected, but this edges
                          request still came back as a free preview. Try refreshing this page or
                          signing out and back in on this device to refresh the session.
                        </>
                      ) : (
                        <>
                          Free preview only – showing a small sample of edges. You’ve used{" "}
                          <b>{edgesMeta.usedToday}</b> / <b>{edgesMeta.limit}</b> free fixture
                          unlocks today.{" "}
                          <Link to="/premium" style={{ color: "#FBBF24" }}>
                            Go Premium →
                          </Link>
                        </>
                      )
                    )}
                  </p>
                )}

                {uniqueBookmakers.length > 1 && (
                  <div className={styles.bookmakerFilter}>
                    <label>Filter by bookmaker: </label>
                    <select
                      value={selectedBookmaker}
                      onChange={(e) => setSelectedBookmaker(e.target.value)}
                    >
                      <option value="All">All</option>
                      {uniqueBookmakers.map((bm) => (
                        <option key={bm} value={bm}>
                          {bm}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className={styles.bookmakerFilter}>
                  <label>Form window: </label>
                  <select
                    value={explainFormWindow}
                    onChange={(e) => setExplainFormWindow(Number(e.target.value))}
                  >
                    <option value={5}>Last 5</option>
                    <option value={10}>Last 10</option>
                    <option value={15}>Last 15</option>
                  </select>
                </div>

                <ul>
                  {filteredEdges.map((e, i) => {
                    const key = `${e.market}-${e.bookmaker}-${i}`;
                    const cacheKey = getExplanationCacheKey(e.market);
                    const exp = explanations[cacheKey];
                    const expErr = explanationErrors[cacheKey];
                    const isExpLoading = explanationLoadingKey === cacheKey;
                    const rowWhy = getRowWhy(e);
                    const statPropMarket = isStatPropMarket(e.market);
                    const showGenericExplanation = !statPropMarket && !rowWhy;
                    const eg = showGenericExplanation ? exp?.form?.expected_goals : null;
                    const notes = getExplainNotes(exp, e);
                    const hasCupBadge = showGenericExplanation && hasCupContext(notes);

                    return (
                      <li key={key}>
                        <b>{formatMarketName(e.market)}</b> — {e.bookmaker} @ <b>{formatOddsBoth(e.price)}</b>{" "}
                        {typeof e.edge === "number" && typeof e.prob === "number" ? (
                          <>
                            ({(e.edge * 100).toFixed(1)}% edge, model p{" "}
                            {(e.prob * 100).toFixed(1)}%)
                          </>
                        ) : null}

                        <button
                          className={styles.whyButton}
                          onClick={() => fetchExplanation(e.market, i, { edge: e })}
                          style={{ marginLeft: 8 }}
                        >
                          {isExpLoading && expandedWhy === i ? "Loading…" : "Why?"}
                        </button>

                        <button
                          className={styles.addBetLink}
                          style={{ marginLeft: 8 }}
                          disabled={placingKey === key}
                          onClick={() => handlePlaceEdge(e, key)}
                        >
                          {placingKey === key ? "Adding…" : "Place Bet"}
                        </button>

                        {expandedWhy === i && (
                          <EdgeExplanationCard
                            edge={e}
                            expectedGoals={eg}
                            notes={notes}
                            recommendation={showGenericExplanation ? exp?.recommendation : null}
                            formWindow={exp?.form_window || explainFormWindow}
                            showFormContext={showGenericExplanation}
                            cupContext={hasCupBadge}
                            loading={isExpLoading}
                            error={expErr}
                            onRetry={() => fetchExplanation(e.market, i, { force: true, edge: e })}
                            formatText={cleanMarketKeysInText}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="scrollX">
                <OddsTable grouped={grouped} />
              </div>

              <FixtureSocialPosts
                fixtureId={fixtureIdNum}
                title={`Posts on ${fixture.home_team} vs ${fixture.away_team}`}
                limit={4}
                dark
              />

              <SportsNewsSection
                mode="fixture"
                fixtureId={fixtureIdNum}
                title="Match News"
                limit={6}
                dark
              />
            </>
          )}

          {activeTab === "live" && (
            <div className={styles.tabContent}>
              {liveLoading && <p style={{ padding: 16, color: "#888" }}>Fetching live data…</p>}
              {liveError && <p style={{ padding: 16, color: "#dc2626" }}>{liveError}</p>}

              {!liveLoading && liveData && !liveData.in_play && (
                <div style={{
                  padding: 24, borderRadius: 12, textAlign: "center",
                  background: "#f5f5f5", color: "#555",
                }}>
                  <p style={{ fontSize: 18, fontWeight: 700, color: "#1e1e2e", marginBottom: 8 }}>
                    Not in play
                  </p>
                  <p style={{ fontSize: 14 }}>
                    {liveData.status === "FT"
                      ? "This match has finished."
                      : liveData.status === "NS"
                        ? "This match hasn't started yet. Come back at kick-off to see live model prices."
                        : `Current status: ${liveData.status || "unknown"}`}
                  </p>
                  <button
                    style={{
                      marginTop: 12, padding: "6px 14px", borderRadius: 999,
                      border: "1px solid #ddd", background: "#fff",
                      color: "#1e1e2e", cursor: "pointer", fontSize: 13,
                    }}
                    onClick={() => refreshLiveOdds("not-in-play")}
                  >
                    Refresh
                  </button>
                  {latestOddsSeen && (
                    <p style={{ marginTop: 10, fontSize: 12 }}>
                      Latest odds timestamp: {latestOddsSeen.toLocaleTimeString()}
                    </p>
                  )}
                  {!!liveRefreshNotice && (
                    <p style={{ marginTop: 8, fontSize: 12 }}>{liveRefreshNotice}</p>
                  )}
                </div>
              )}

              {!liveLoading && liveData && liveData.in_play && (() => {
                const { score, elapsed, model, status } = liveData;
                // Build rows: model fair price vs best bookmaker live odds (from the odds already loaded)
                const liveMarkets = ["HOME_WIN", "DRAW", "AWAY_WIN", "O2.5", "U2.5", "BTTS_Y", "BTTS_N"];
                const MARKET_LABEL = {
                  HOME_WIN: fixture.home_team,
                  AWAY_WIN: fixture.away_team,
                };

                // Best bookmaker price per market from the already-loaded odds
                const bestByMarket = {};
                for (const o of odds) {
                  const cur = bestByMarket[o.market];
                  const price = Number(o.price);
                  if (!cur || price > cur.price) {
                    bestByMarket[o.market] = { price, bookmaker: o.bookmaker };
                  }
                }

                return (
                  <div>
                    {/* Scoreboard */}
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      gap: 16, padding: "18px 0 12px", flexWrap: "wrap",
                    }}>
                      <div style={{ textAlign: "center", minWidth: 100 }}>
                        <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>{fixture.home_team}</div>
                        <div style={{ fontSize: 36, fontWeight: 800, color: "#1e1e2e" }}>{score.home}</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{
                          display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                          color: "#fff", background: "#16a34a", borderRadius: 999, padding: "2px 8px",
                        }}>LIVE</span>
                        <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>
                          {status === "HT" ? "Half Time" : `${elapsed}'`}
                        </div>
                      </div>
                      <div style={{ textAlign: "center", minWidth: 100 }}>
                        <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>{fixture.away_team}</div>
                        <div style={{ fontSize: 36, fontWeight: 800, color: "#1e1e2e" }}>{score.away}</div>
                      </div>
                    </div>

                    {/* Model fair prices vs bookmaker odds table */}
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#555", background: "#f5f5f5" }}>Market</th>
                            <th style={{ textAlign: "right", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#555", background: "#f5f5f5" }}>Model p</th>
                            <th style={{ textAlign: "right", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#555", background: "#f5f5f5" }}>Fair Price</th>
                            <th style={{ textAlign: "right", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#555", background: "#f5f5f5" }}>Best Book</th>
                            <th style={{ textAlign: "right", padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize: 13, color: "#555", background: "#f5f5f5" }}>Edge</th>
                          </tr>
                        </thead>
                        <tbody>
                          {liveMarkets.map((mkt) => {
                            const m = model[mkt];
                            if (!m) return null;
                            const best = bestByMarket[mkt];
                            // edge = (prob * bookPrice) - 1
                            const edge = best && m.prob > 0 ? (m.prob * best.price - 1) : null;
                            const hasEdge = edge !== null && edge > 0;

                            return (
                              <tr key={mkt} style={{ background: hasEdge ? "rgba(22,163,74,0.12)" : undefined }}>
                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, color: "#333" }}>
                                  {MARKET_LABEL[mkt] || formatMarketName(mkt)}
                                </td>
                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, color: "#333", textAlign: "right" }}>
                                  {(m.prob * 100).toFixed(1)}%
                                </td>
                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, color: "#333", textAlign: "right" }}>
                                  {m.fair_price?.toFixed(2) ?? "—"}
                                </td>
                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, textAlign: "right" }}>
                                  {best ? (
                                    <span style={{ color: "#333" }}>
                                      {best.price.toFixed(2)} <span style={{ fontSize: 11, color: "#888" }}>({best.bookmaker})</span>
                                    </span>
                                  ) : <span style={{ color: "#aaa" }}>—</span>}
                                </td>
                                <td style={{ padding: "10px 8px", borderBottom: "1px solid rgba(0,0,0,0.06)", fontSize: 14, textAlign: "right", fontWeight: hasEdge ? 700 : 400, color: hasEdge ? "#16a34a" : edge !== null && edge < 0 ? "#888" : "#aaa" }}>
                                  {edge !== null ? `${(edge * 100).toFixed(1)}%` : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Refresh + disclaimer */}
                    <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <button
                        style={{
                          padding: "6px 14px", borderRadius: 999,
                          border: "1px solid #ddd", background: "#fff",
                          color: "#1e1e2e", cursor: "pointer", fontSize: 13,
                        }}
                        onClick={() => refreshLiveOdds("in-play")}
                      >
                        Refresh
                      </button>
                      <p style={{ fontSize: 11, color: "#999", margin: 0 }}>
                        Model prices update on refresh. Book prices are from the last odds poll.
                      </p>
                      {latestOddsSeen && (
                        <p style={{ fontSize: 11, color: "#999", margin: 0 }}>
                          Last odds seen: {latestOddsSeen.toLocaleTimeString()}
                        </p>
                      )}
                      {lastLiveRefreshAt && (
                        <p style={{ fontSize: 11, color: "#999", margin: 0 }}>
                          Refreshed: {lastLiveRefreshAt.toLocaleTimeString()}
                        </p>
                      )}
                      {!!liveRefreshNotice && (
                        <p style={{ fontSize: 11, color: "#999", margin: 0 }}>{liveRefreshNotice}</p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === "h2h" && (
            <div className={styles.tabContent}>
              {h2hLoading ? (
                <p style={{ padding: 16, color: "rgba(255,255,255,0.5)" }}>Loading H2H data...</p>
              ) : (
                <H2HSection
                  data={h2hData}
                  homeName={fixture.home_team}
                  awayName={fixture.away_team}
                />
              )}
            </div>
          )}

          {activeTab === "table" && (
            <div className={styles.standingsSection}>
              {leagueTableLoading ? (
                <p className={styles.standingsMessage}>Loading standings…</p>
              ) : leagueTableError ? (
                <p className={styles.standingsMessage}>{leagueTableError}</p>
              ) : leagueTableGroups.length ? (
                <>
                  <div className={styles.worldCupStandingsHeader}>
                    <div>
                      <span>FIFA World Cup 2026</span>
                      <h2>Group standings</h2>
                    </div>
                    <Link to="/league/WC?tab=groups">Open tournament hub</Link>
                  </div>
                  <div className={styles.worldCupStandingsGrid}>
                    {leagueTableGroups.map((group) => (
                      <section key={group.name} className={styles.worldCupStandingsCard}>
                        <div className={styles.worldCupStandingsTitle}>
                          <strong>{group.name}</strong>
                          <span>{group.rows.length} teams</span>
                        </div>
                        <StandingsTable
                          rows={group.rows}
                          homeTeam={fixture.home_team}
                          awayTeam={fixture.away_team}
                          compact
                        />
                      </section>
                    ))}
                  </div>
                </>
              ) : leagueTable.length ? (
                <StandingsTable
                  rows={leagueTable}
                  homeTeam={fixture.home_team}
                  awayTeam={fixture.away_team}
                />
              ) : (
                <p className={styles.standingsMessage}>No standings are available for this competition yet.</p>
              )}
            </div>
          )}

          {activeTab === "predictions" && <PredictionsSection fixtureId={id} />}

          {activeTab === "lineups" && (
            <div className={styles.lineupsTabShell}>
              <div className={styles.subTabs}>
                {["lineup", "injuries", "props"].map((sub) => (
                  <button
                    key={sub}
                    className={lineupTab === sub ? styles.activeTab : ""}
                    onClick={() => setLineupTab(sub)}
                  >
                    {sub.charAt(0).toUpperCase() + sub.slice(1)}
                  </button>
                ))}
              </div>

              {lineupTab === "lineup" && <LineupsSection fixtureId={id} fallbackLineups={fixture?.lineups} />}
              {lineupTab === "injuries" && <InjuriesSection fixtureId={id} />}
              {lineupTab === "props" && (
                <PlayerPropsSection
                  fixtureId={id}
                  homeTeam={fixture.home_team}
                  awayTeam={fixture.away_team}
                />
              )}
            </div>
          )}

          {activeTab === "stats" && (
            <MatchStatsSection
              fixtureId={id}
              homeTeam={fixture.home_team}
              awayTeam={fixture.away_team}
            />
          )}

          {activeTab === "players" && (
            <PlayersSection
              fixtureId={id}
              homeTeam={fixture.home_team}
              awayTeam={fixture.away_team}
            />
          )}

          {activeTab === "events" && (
            <EventsSection
              fixtureId={id}
              initialEvents={fixture?.events}
              homeTeamId={fixture?.provider_home_team_id ?? fixture?.home_team_id ?? fixture?.home_id}
              awayTeamId={fixture?.provider_away_team_id ?? fixture?.away_team_id ?? fixture?.away_id}
            />
          )}
        </div>

        <div className={styles.side}>
          <Poll fixtureId={id} homeTeam={fixture.home_team} awayTeam={fixture.away_team} />

          {formData && (
            <div className={`${styles.formSection} ${styles.staticForm}`}>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <label style={{ fontSize: 12, color: "#666" }}>Form:</label>
                <select
                  value={formN}
                  onChange={(e) => setFormN(Number(e.target.value))}
                  style={{ fontSize: 12 }}
                >
                  {[3, 5, 7, 10].map((v) => (
                    <option key={v} value={v}>
                      last {v}
                    </option>
                  ))}
                </select>

                <label style={{ fontSize: 12, color: "#666", marginLeft: 6 }}>
                  Scope:
                </label>
                <select
                  value={formScopeAll}
                  onChange={(e) => setFormScopeAll(Number(e.target.value))}
                  style={{ fontSize: 12 }}
                >
                  <option value={0}>This competition</option>
                  <option value={1}>All competitions</option>
                </select>
              </div>

              <FormBreakdown
                home={{ summary: formData.home.summary, recent: formData.home.recent }}
                away={{ summary: formData.away.summary, recent: formData.away.recent }}
                homeName={fixture.home_team}
                awayName={fixture.away_team}
                n={formData.n}
              />
            </div>
          )}

          <TeamContextPanel
            fixtureId={fixtureIdNum}
            homeTeam={fixture.home_team}
            awayTeam={fixture.away_team}
          />

          <LeagueFixtures fixtures={leagueFixtures} />
        </div>
      </div>
    </div>
  );
};

export default FixturePage;
